import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Image as ImageIcon, Trash2, Plus, ChevronUp, ChevronDown, LogOut, ExternalLink,
  Palette, Phone, ListOrdered, Code2, Inbox, Loader2, ShieldAlert,
} from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Loader = () => (
  <div className="flex min-h-screen items-center justify-center bg-ink">
    <Loader2 className="h-8 w-8 animate-spin text-cyan-400" data-testid="admin-loader" />
  </div>
);

const Field = ({ label, children, testid }) => (
  <div className="space-y-2">
    <Label className="text-xs font-medium uppercase tracking-wider text-slate-400" data-testid={testid}>{label}</Label>
    {children}
  </div>
);

const UploadField = ({ label, value, onChange, testid, tall = false }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/admin/upload", fd);
      onChange(r.data.url);
      toast.success(`${label} uploaded — press Save changes to publish`);
    } catch {
      toast.error("Upload failed");
    }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-4 rounded-xl border border-white/10 bg-ink2/60 p-4" data-testid={`upload-field-${testid}`}>
      {value ? (
        <img src={assetUrl(value)} alt={label} className={`${tall ? "h-16 w-28" : "h-12 w-12"} shrink-0 rounded-lg border border-white/10 object-cover`} data-testid={`upload-preview-${testid}`} />
      ) : (
        <div className={`flex ${tall ? "h-16 w-28" : "h-12 w-12"} shrink-0 items-center justify-center rounded-lg bg-surface text-slate-500`}>
          <ImageIcon className="h-5 w-5" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium capitalize text-white">{label}</p>
        <p className="truncate text-xs text-slate-500">{value || "No file yet"}</p>
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`admin-${testid}-input`} />
      <Button variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={busy} data-testid={`admin-${testid}-upload-button`}>
        {busy ? "Uploading…" : "Upload"}
      </Button>
      {value && (
        <Button variant="ghost" size="sm" onClick={() => onChange("")} data-testid={`admin-${testid}-clear-button`}>
          Clear
        </Button>
      )}
    </div>
  );
};

const SectionTitle = ({ children }) => <h3 className="font-display text-lg font-bold text-white">{children}</h3>;

const IMAGE_LABELS = {
  hero: "Hero image (Home)",
  "service-branding": "1. Branding — service photo (Services)",
  "service-software": "2. Web & Software — service photo (Services)",
  "service-advertising": "3. Advertising & Media — service photo (Services)",
  "service-social": "4. Social Media — service photo (Services)",
  "service-analytics": "5. Analytics & Insights — service photo (Services)",
  "service-tech": "6. Technology & Infrastructure — service photo (Services)",
  "service-events": "7. Events & Concerts — photo (Services, Events, Home)",
  "service-radiotv": "8. Radio & TV — service photo (Services)",
  concert: "Concerts & Festivals — Events gallery",
  stage: "Stage & Lighting — Events gallery",
  broadcast: "Live Radio — Events gallery",
  team: "Corporate Events — Events gallery",
  office: "Launches & Showcases — Events gallery",
};

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(undefined);
  const [draft, setDraft] = useState(null);
  const [tab, setTab] = useState("branding");
  const [messages, setMessages] = useState([]);
  const [saving, setSaving] = useState(false);
  const deniedByParam = location.search.includes("error=403");

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/settings").then((r) => setDraft(r.data)).catch(() => {});
    api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => {});
  }, [user]);

  if (user === undefined) return <Loader />;
  if (!user)
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-6" data-testid="admin-login-screen">
        <div className="w-full max-w-md rounded-3xl border border-white/10 bg-ink2/70 p-10 text-center">
          <LogoMark className="mx-auto h-14 w-14" />
          <h1 className="mt-6 font-display text-2xl font-bold text-white">Cyber Planet Admin</h1>
          <p className="mt-2 text-sm text-slate-400" data-testid="admin-login-subtitle">
            Sign in with Google to manage the website.
          </p>
          {deniedByParam && (
            <div className="mt-6 flex items-start gap-3 rounded-xl border border-rose-400/30 bg-rose-400/10 p-4 text-left" data-testid="admin-access-denied-alert">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-rose-400" />
              <p className="text-xs text-rose-200">This Google account is not authorized. Only the site owner's email has access.</p>
            </div>
          )}
          <button
            onClick={googleLogin}
            data-testid="admin-google-login-button"
            className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-slate-900 transition hover:bg-slate-200"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
              <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l-.02.15 3.5 2.7.24.03c2.2-2 3.5-5 3.5-8.6z" />
              <path fill="#34A853" d="M12 24c3.2 0 5.9-1 7.9-2.9l-3.8-2.9c-1 .7-2.4 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5l-.14.01-3.1 2.4-.04.11C4.1 21.3 7.8 24 12 24z" />
              <path fill="#FBBC05" d="M5.3 14.4c-.25-.7-.4-1.5-.4-2.4s.15-1.7.4-2.4l-.01-.16-3.2-2.4-.1.08C1.1 8.6.6 10.2.6 12s.5 3.4 1.4 4.9l3.3-2.5z" />
              <path fill="#EA4335" d="M12 4.6c2.2 0 3.7 1 4.5 1.7l3.3-3.2C17.9 1.2 15.2 0 12 0 7.8 0 4.1 2.7 2 6.5l3.3 2.5c.9-2.9 3.6-4.4 6.7-4.4z" />
            </svg>
            Continue with Google
          </button>
        </div>
      </div>
  );
  if (!draft) return <Loader />;

  const setPath = (path, value) =>
    setDraft((d) => {
      const parts = path.split(".");
      const clone = { ...d, brand: { ...d.brand }, contact: { ...d.contact }, seo: { ...d.seo }, images: { ...d.images }, menu: [...d.menu], social: [...d.social] };
      let cur = clone;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...cur[parts[i]] };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return clone;
    });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/settings", draft);
      setDraft(r.data);
      toast.success("Settings saved — live on the site now.");
    } catch {
      toast.error("Save failed. Try again.");
    }
    setSaving(false);
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  const TABS = [
    { id: "branding", label: "Branding & Media", icon: Palette },
    { id: "contact", label: "Contact & Social", icon: Phone },
    { id: "menu", label: "Menu Items", icon: ListOrdered },
    { id: "seo", label: "SEO & Sitemap", icon: Code2 },
    { id: "inquiries", label: "Inquiries", icon: Inbox },
  ];

  const move = (arr, i, dir) => {
    const clone = [...arr];
    const j = i + dir;
    if (j < 0 || j >= clone.length) return clone;
    [clone[i], clone[j]] = [clone[j], clone[i]];
    return clone;
  };

  return (
    <div className="min-h-screen bg-ink pb-32" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0B0F17]/95">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <LogoMark className="h-8 w-8" />
            <span className="font-display text-sm font-bold tracking-[0.08em] text-white">CYBER PLANET — ADMIN</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" target="_blank" className="inline-flex items-center gap-1.5 text-xs text-slate-400 transition hover:text-cyan-300" data-testid="admin-view-site-link">
              View site <ExternalLink className="h-3.5 w-3.5" />
            </Link>
            <Button variant="ghost" size="sm" onClick={logout} className="text-slate-400 hover:text-white" data-testid="admin-logout-button">
              <LogOut className="mr-1.5 h-4 w-4" /> Log out
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <p className="text-xs text-slate-500" data-testid="admin-signed-in-as">Signed in as <span className="text-cyan-300">{user.email}</span></p>

        <div className="mt-6 flex gap-2 overflow-x-auto pb-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`inline-flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition ${active ? "bg-cyan-400 text-ink" : "border border-white/10 text-slate-300 hover:border-cyan-400/40 hover:text-white"}`}
              >
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-8 space-y-6">
          {tab === "branding" && (
            <>
              <div className="grid grid-cols-1 gap-5 rounded-2xl border border-white/10 bg-ink2/50 p-6 sm:grid-cols-2" data-testid="admin-branding-section">
                <Field label="Company name">
                  <Input value={draft.brand.company_name} onChange={(e) => setPath("brand.company_name", e.target.value)} data-testid="admin-company-name-input" />
                </Field>
                <Field label="Tagline">
                  <Input value={draft.brand.tagline} onChange={(e) => setPath("brand.tagline", e.target.value)} data-testid="admin-tagline-input" />
                </Field>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="admin-logo-section">
                <UploadField label="Logo" value={draft.brand.logo_url} onChange={(v) => setPath("brand.logo_url", v)} testid="logo" />
                <UploadField label="Favicon" value={draft.brand.favicon_url} onChange={(v) => setPath("brand.favicon_url", v)} testid="favicon" />
              </div>
              <SectionTitle>Images — where each one appears</SectionTitle>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="admin-images-section">
                {Object.entries(draft.images || {}).map(([key, url]) => (
                  <UploadField key={key} label={IMAGE_LABELS[key] || key} value={url} onChange={(v) => setPath(`images.${key}`, v)} testid={`image-${key}`} tall />
                ))}
              </div>
            </>
          )}

          {tab === "contact" && (
            <>
              <div className="grid grid-cols-1 gap-5 rounded-2xl border border-white/10 bg-ink2/50 p-6 sm:grid-cols-3" data-testid="admin-contact-section">
                <Field label="Phone number">
                  <Input value={draft.contact.phone} onChange={(e) => setPath("contact.phone", e.target.value)} placeholder="+1 (___) ___-____" data-testid="admin-phone-input" />
                </Field>
                <Field label="Contact email">
                  <Input value={draft.contact.email} onChange={(e) => setPath("contact.email", e.target.value)} data-testid="admin-email-input" />
                </Field>
                <Field label="Address">
                  <Input value={draft.contact.address} onChange={(e) => setPath("contact.address", e.target.value)} data-testid="admin-address-input" />
                </Field>
              </div>
              <SectionTitle>Social media</SectionTitle>
              <div className="space-y-3" data-testid="admin-social-section">
                {draft.social.map((s, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-ink2/60 p-4">
                    <Input value={s.platform} onChange={(e) => setPath(`social`, draft.social.map((x, j) => (j === i ? { ...x, platform: e.target.value } : x)))} placeholder="platform" className="w-32 bg-ink/60" data-testid={`admin-social-platform-input-${i}`} />
                    <Input value={s.url} onChange={(e) => setPath(`social`, draft.social.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))} placeholder="https://instagram.com/yourname" className="flex-1 bg-ink/60" data-testid={`admin-social-url-input-${i}`} />
                    <Button variant="ghost" size="icon" onClick={() => setPath("social", draft.social.filter((_, j) => j !== i))} data-testid={`admin-social-delete-${i}`} aria-label="Delete social">
                      <Trash2 className="h-4 w-4 text-rose-400" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setPath("social", [...draft.social, { platform: "", url: "" }])} data-testid="admin-social-add-button">
                  <Plus className="mr-1.5 h-4 w-4" /> Add social link
                </Button>
              </div>
            </>
          )}

          {tab === "menu" && (
            <div className="space-y-3" data-testid="admin-menu-section">
              <p className="text-sm text-slate-400">These items drive the navbar and footer of the public site.</p>
              {draft.menu.map((m, i) => (
                <div key={i} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-ink2/60 p-4">
                  <Input value={m.label} onChange={(e) => setPath("menu", draft.menu.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} placeholder="Label" className="w-36 bg-ink/60" data-testid={`admin-menu-label-input-${i}`} />
                  <Input value={m.path} onChange={(e) => setPath("menu", draft.menu.map((x, j) => (j === i ? { ...x, path: e.target.value } : x)))} placeholder="/path" className="w-40 bg-ink/60" data-testid={`admin-menu-path-input-${i}`} />
                  <div className="flex items-center gap-2">
                    <Switch checked={m.enabled !== false} onCheckedChange={(v) => setPath("menu", draft.menu.map((x, j) => (j === i ? { ...x, enabled: v } : x)))} data-testid={`admin-menu-enabled-switch-${i}`} />
                    <span className="text-xs text-slate-400">{m.enabled !== false ? "Visible" : "Hidden"}</span>
                  </div>
                  <div className="ml-auto flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setPath("menu", move(draft.menu, i, -1))} data-testid={`admin-menu-up-${i}`} aria-label="Move up"><ChevronUp className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => setPath("menu", move(draft.menu, i, 1))} data-testid={`admin-menu-down-${i}`} aria-label="Move down"><ChevronDown className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => setPath("menu", draft.menu.filter((_, j) => j !== i))} data-testid={`admin-menu-delete-${i}`} aria-label="Delete item"><Trash2 className="h-4 w-4 text-rose-400" /></Button>
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setPath("menu", [...draft.menu, { label: "New page", path: "/", enabled: true }])} data-testid="admin-menu-add-button">
                <Plus className="mr-1.5 h-4 w-4" /> Add menu item
              </Button>
            </div>
          )}

          {tab === "seo" && (
            <div className="space-y-6" data-testid="admin-seo-section">
              <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-5" data-testid="admin-seo-links-box">
                <p className="font-display text-sm font-bold text-white">Files to submit in Google Search Console</p>
                <p className="mt-1 text-xs text-slate-400">Search Console → Sitemaps → paste the sitemap link below. These links use your Site URL field.</p>
                <ul className="mt-3 space-y-1.5 font-mono text-xs">
                  {["sitemap.xml", "robots.txt", "llms.txt"].map((f) => (
                    <li key={f}>
                      <a
                        href={`${((draft.seo.site_url || "https://cyberplanet.online").replace(/\/+$/, ""))}/${f}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-cyan-300 hover:underline"
                        data-testid={`admin-seo-file-link-${f.replace(".", "-")}`}
                      >
                        {((draft.seo.site_url || "https://cyberplanet.online").replace(/\/+$/, ""))}/{f}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="grid grid-cols-1 gap-5 rounded-2xl border border-white/10 bg-ink2/50 p-6 sm:grid-cols-2">
                <Field label="Site URL (canonical, sitemap & share tags)">
                  <Input value={draft.seo.site_url || ""} onChange={(e) => setPath("seo.site_url", e.target.value)} placeholder="https://cyberplanet.online" data-testid="admin-site-url-input" />
                </Field>
                <UploadField label="Share image — og:image (1200×630 recommended)" value={draft.seo.og_image || ""} onChange={(v) => setPath("seo.og_image", v)} testid="og-image" tall />
              </div>
              <SectionTitle>Page titles & descriptions — what Google shows in results</SectionTitle>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {[["home", "Home"], ["services", "Services"], ["events", "Events"], ["contact", "Contact"]].map(([k, label]) => (
                  <div key={k} className="space-y-3 rounded-2xl border border-white/10 bg-ink2/50 p-5" data-testid={`admin-page-seo-${k}`}>
                    <p className="font-display text-sm font-bold text-white">{label}</p>
                    <Field label={`Title — ${(draft.seo.pages?.[k]?.title || "").length}/60 chars`}>
                      <Input value={draft.seo.pages?.[k]?.title || ""} onChange={(e) => setPath(`seo.pages.${k}.title`, e.target.value)} data-testid={`admin-page-title-${k}-input`} />
                    </Field>
                    <Field label={`Description — ${(draft.seo.pages?.[k]?.description || "").length}/160 chars`}>
                      <Textarea value={draft.seo.pages?.[k]?.description || ""} onChange={(e) => setPath(`seo.pages.${k}.description`, e.target.value)} rows={3} className="bg-ink/60 text-sm" data-testid={`admin-page-desc-${k}-textarea`} />
                    </Field>
                  </div>
                ))}
              </div>
              <SectionTitle>Analytics & verification</SectionTitle>
              <div className="grid grid-cols-1 gap-5 rounded-2xl border border-white/10 bg-ink2/50 p-6 sm:grid-cols-2">
                <Field label="Google Analytics ID (G-… / GTM-… or paste script)">
                  <Input value={draft.seo.ga_id} onChange={(e) => setPath("seo.ga_id", e.target.value)} placeholder="G-XXXXXXXXXX" data-testid="admin-ga-input" />
                </Field>
                <Field label="Search Console verification (content token or meta tag)">
                  <Input value={draft.seo.gsc_verification} onChange={(e) => setPath("seo.gsc_verification", e.target.value)} placeholder="abc123… or <meta …>" data-testid="admin-gsc-input" />
                </Field>
              </div>
              <Field label="Custom <head> script">
                <Textarea value={draft.seo.header_script} onChange={(e) => setPath("seo.header_script", e.target.value)} rows={4} className="bg-ink/60 font-mono text-xs" placeholder="<script>…</script>" data-testid="admin-header-script-textarea" />
              </Field>
              <Field label="Custom <body> script">
                <Textarea value={draft.seo.body_script} onChange={(e) => setPath("seo.body_script", e.target.value)} rows={4} className="bg-ink/60 font-mono text-xs" placeholder="<script>…</script>" data-testid="admin-body-script-textarea" />
              </Field>
              <Field label="Custom footer script">
                <Textarea value={draft.seo.footer_script} onChange={(e) => setPath("seo.footer_script", e.target.value)} rows={4} className="bg-ink/60 font-mono text-xs" placeholder="<script>…</script>" data-testid="admin-footer-script-textarea" />
              </Field>
              <p className="text-xs text-slate-500">Everything here goes live the moment you press Save changes.</p>
            </div>
          )}

          {tab === "inquiries" && (
            <div className="space-y-4" data-testid="admin-inquiries-section">
              {messages.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-white/10 p-12 text-center text-sm text-slate-500" data-testid="admin-inquiries-empty">
                  No inquiries yet — messages from the contact page will land here.
                </div>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className="rounded-2xl border border-white/10 bg-ink2/60 p-6" data-testid={`admin-inquiry-${m.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-medium text-white">{m.name} <span className="ml-2 text-xs text-slate-500">{m.email}{m.phone ? ` · ${m.phone}` : ""}</span></p>
                        <p className="mt-0.5 text-xs text-slate-500">{new Date(m.created_at).toLocaleString()}</p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={async () => { await api.delete(`/admin/messages/${m.id}`).catch(() => {}); setMessages(messages.filter((x) => x.id !== m.id)); }} data-testid={`admin-inquiry-delete-${m.id}`} aria-label="Delete inquiry">
                        <Trash2 className="h-4 w-4 text-rose-400" />
                      </Button>
                    </div>
                    <p className="mt-4 whitespace-pre-wrap text-sm text-slate-300">{m.message}</p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[#0B0F17]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <p className="text-xs text-slate-500">Changes go live the moment you save.</p>
          <Button onClick={save} disabled={saving} data-testid="admin-save-settings-button" className="rounded-full bg-cyan-400 px-8 font-semibold text-ink hover:bg-cyan-300">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
